<template>
	
    <div class="vpay__step" v-loading="loading">
		
		<div class="vpay__input-container vpay__verification-instructions">
			<ion-icon class="vpay__verification-instructions-icon" :name="verificationInstructionsIcon"></ion-icon>
			<p class="vpay__verification-instructions">{{ verificationInstructions }}</p>
		</div>
		
		<div v-if="!receivedCode && !codeVerified" class="vpay__input-container">
			<input v-if="userHasPhoneNumber" class="vpay__input vpay__mobile-number vpay__mobile-number--disabled" type="text" v-model="mobileNumberObfuscated" readonly>
			<the-mask v-else class="vpay__input vpay__mobile-number" v-model="mobileNumber" :style="inputStyle" mask="+61 #### ### ###" type="tel" placeholder="Mobile Number"></the-mask>
		</div>
		
		<div v-if="receivedCode && !codeVerified" class="vpay__input-container">
			<the-mask class="vpay__input vpay__verification-code" v-model="verificationCode" :style="inputStyle" mask="# # # # # #" type="text" placeholder="- - - - - -"></the-mask>
		</div>
		
		<div class="vpay__input-container">
			<el-button class="vpay__next-button" @click="onClickNext" round>{{ nextButtonLabel }}</el-button>
		</div>
		
    </div>
	
</template>

<script>
	
	import { TheMask } from "vue-the-mask";
	
	const PNF = require("google-libphonenumber").PhoneNumberFormat;
	const phoneUtil = require("google-libphonenumber").PhoneNumberUtil.getInstance();
	
    export default {
		
        name: "VerificationCode",
		
		components: { TheMask },
		
		props: {
			
			value: {
				type: String,
				default: ""
			},
			
			clientId: {
				type: String,
				default: "",
			},
			
			tintColour: {
				type: String,
				default: "",
			},
			
			userPhoneNumber: {
				type: String,
				default: "",
			},
			
			apiUrl: {
				type: String,
				default: "",
			},
			
			resetAfterVerification: {
				type: Boolean,
				default: false,
			},
			
			scope: {
				type: String,
				default: "",
			},

			recurringPaymentPhone: {
				type: String,
				default: "",
			}
			
		},
		
		watch: {
			
			mobileNumber: {
				handler() {
					this.$emit("change", this.mobileNumber);
					this.$emit("input", this.mobileNumber);
				}
			}
			
		},
		
		methods: {
			
			reset() {
				
				this.mobileNumber = this.userPhoneNumber;
				this.mobileNumberValidated = "";
				
				this.verificationCode = "";
				this.receivedCode = false;
				this.codeVerified = false;
				
			},
			
			onClickNext() {
				
				if (this.receivedCode === false) {
					
					if (this.mobileNumber) {
						
						// Validate the phone number.
						
						const mobileNumber = phoneUtil.parseAndKeepRawInput(this.mobileNumber, "AU");
						const isValidMobileNumber = phoneUtil.isValidNumber(mobileNumber);
						const mobileNumberTrimmed = phoneUtil.format(mobileNumber, PNF.NATIONAL).replace(/ /g, "");
						
						// Valid mobile number?
						
						if (isValidMobileNumber) {

							if (this.recurringPaymentPhone == "" || `0${this.recurringPaymentPhone}` == mobileNumberTrimmed) {

								// Emit event.
															
								this.$emit("event", { name: "sendCode", mobileNumber: mobileNumberTrimmed });
								
								// Store validated mobile number.
								
								this.mobileNumberValidated = mobileNumberTrimmed;
								
								// API request.
								
								this.axios.post(`${this.apiUrl}/send-verification-code`,
									{ scope: this.scope, mobile_number: mobileNumberTrimmed },
									{ headers: { "Vinteract-Client-Id": this.clientId }
								}).then((r) => {
									if (r.data.success) {
										this.$emit("event", { name: "receiveCode" });
										this.receivedCode = true;
									} else {
										this.$emit("event", { error: "Uh oh... our system couldn't send you the verification code. Please try again." });
									}
								}).catch(() => {
									this.$emit("event", { error: "Uh oh... our system couldn't send you the verification code. Please try again." });
								});
							} else {
								this.$emit("event", { error: "The mobile number you entered does not match our record." });
							}
							
						} else {
							
							this.$emit("event", { error: "Please enter a valid Australian mobile number." });
							
						}
						
					} else {
						
						this.$emit("event", { error: "Please enter a valid Australian mobile number." });
						
					}
					
				} else if (this.codeVerified === false) {
					
					// Emit event.
					
					this.$emit("event", { name: "verifyCode" });
					
					// Verify the code.
					
					this.axios.post(`${this.apiUrl}/verify-code`,
						{ scope: this.scope, code: this.verificationCode, mobile_number: this.mobileNumberValidated },
						{ headers: { "Vinteract-Client-Id": this.clientId } },
					).then((r) => {
						if (r.data.success) {
							this.$emit("event", { name: "codeVerified" });
							if (this.resetAfterVerification) {
								this.reset();
							}
						} else {
							this.$emit("event", { error: r.data.message });
						}
					}).catch((e) => {
						this.$emit("event", { error: e.response.data.message });
					});
					
				}
				
			},
			
			obfuscatePhoneNumber(mobileNumber) {
				if (mobileNumber) {
					const trimmed = mobileNumber.toString().replace(/\s/g, "");
					return `+61 •••• ••• ${trimmed.substring(trimmed.length - 3)}`;
				}
				return "";
			},
			
		},
		
		computed: {
			
			userHasPhoneNumber() {
				return this.userPhoneNumber != null && this.userPhoneNumber != "";
			},
			
			verificationInstructionsIcon() {
				if (this.receivedCode === false) {
					return "ios-lock";
				} else if (this.codeVerified === false) {
					return "ios-key";
				}
				return "ios-sunny";
			},
			
			verificationInstructions() {
				if (this.receivedCode === false) {
					return "Before you can continue we need to verify your mobile phone.";
				} else if (this.codeVerified === false) {
					return "Enter the verification code that was sent to your mobile phone.";
				}
				return "Success! Your mobile number has been verified.";
			},
			
			nextButtonLabel() {
				if (this.receivedCode === false) {
					return "Send Code";
				} else if (this.codeVerified === false) {
					return "Verify Code";
				}
				return "Next";
			},
			
			inputStyle() {
				return {
					"border-color": this.tintColour,
					"color": this.tintColour,
				};
			},
			
		},
		
		data() {
			
			return {
				
				loading: false,
				
				mobileNumber: this.userPhoneNumber,
				mobileNumberValidated: "",
				mobileNumberObfuscated: this.obfuscatePhoneNumber(this.userPhoneNumber),
				
				verificationCode: "",
				
				receivedCode: false,
				
				codeVerified: false,
				
			};
			
		}
		
    }
	
</script>

<style>
	
	.vpay__input-container {
		padding: 16px;
		padding-bottom: 0;
	}

	.vpay__input {
		display: block;
		padding: 20px;
		border: 0;
		color: #333333;
		font-size: 32px;
		font-weight: 400;
		width: 100%;
		text-align: center;
		/*box-sizing: border-box;*/
		border-radius: 4px;
		border: 2px solid rgba(0, 0, 0, 0.1);
		box-sizing: border-box;
	}

	.vpay__input-note {
		text-align: center;
		font-size: 12px;
		/*color: #E6A23C;*/
		margin: 4px 0 0 0;
		opacity: 0.5;
	}

	.vpay__input:focus {
		outline: none;
	}

	.vpay__input::placeholder {
		color: #CCC;
	}

	.vpay__input--small {
		padding: 8px 12px;
		text-align: left;
		font-size: 16px;
	}

	.vpay__verification-instructions {
		text-align: center;
		font-size: 20px;
		padding: 0;
		margin: 0 0 20px 0;
	}

	.vpay__verification-instructions-icon {
		font-size: 48px;
		margin: 0 0 20px 0;
	}

	.vpay__verification-code {
		width: 240px;
		margin: 0 auto;
	}

	.vpay__mobile-number--disabled {
		border: 0;
	}

	.vpay__next-button {
		display: block;
		margin: 0 auto;
	}
	
</style>
